import React from 'react';
import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';
import toast from 'react-hot-toast';
import { TRPCClientError } from '@trpc/client';
import { style } from '../../../components/Shared/Style';
import { Card } from '../../../components/Layout/Card';
import { L } from '../../../lib/i18n';
import { Button } from '@ourliving/ourliving-ui';
import { useGetSupplierById } from './hooks/supplierQueries';
import { Loading } from '../../../components/Loading/Loading';
import useEditSupplier from './hooks/useEditSupplier';
import SupplierForm from './components/SupplierForm';
import useDeleteSupplier from './hooks/useDeleteSupplier';
import ConfirmationButton from '../../../components/Buttons/ConfirmButton';

const Content = styled.div({
    borderRadius: style.roundCornerSize.small,
    padding: style.margin.l,
    paddingBottom: style.margin.xs,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
});

const ButtonContainer = styled.div({
    width: 'auto',
    marginTop: style.margin.m,
    display: 'flex',
    justifyContent: 'flex-end',
});

const RestyledCard = styled(Card)({
    [`@media screen and (min-width: 1200px)`]: {
        width: '50%',
    },
    width: '100%',
});

export const SupplierDetailPage = () => {
    const { id } = useParams<{
        id: string;
    }>();
    return id ? <SupplierDetail id={+id} /> : null;
};

const SupplierDetail = ({ id }: { id: number }) => {
    const { data: supplier, isLoading } = useGetSupplierById({ id: id, keepPreviousData: true });
    const navigate = useNavigate();

    const { mutate } = useEditSupplier();
    const onSubmit = (supplier: { name: string }) =>
        mutate(
            { supplier: { ...supplier, id: id } },
            {
                onSuccess: () => {
                    toast.success(`${L('supplier')} ${supplier.name} ${L('updated').toLocaleLowerCase()}`);
                },
                onError: (err) => {
                    if (err instanceof TRPCClientError) {
                        if (err.message.split(' ')[0] === '23505') {
                            toast.error(L('unique_name_error'));
                            return;
                        }
                        toast.error(L('pg_unkown'));
                    }
                },
            },
        );

    const { mutate: remove } = useDeleteSupplier();

    if (isLoading) return <Loading />;

    return (
        <>
            <Content>
                <Card padding="0" width="49%">
                    {L('contact')}
                </Card>
            </Content>
            <Content>
                <RestyledCard background={style.colors.white}>
                    <SupplierForm supplier={supplier} onSubmit={onSubmit}>
                        <ButtonContainer>
                            <ConfirmationButton
                                props={{
                                    onConfirm: () => {
                                        if (!supplier?.id) {
                                            return;
                                        }
                                        remove(
                                            {
                                                id: +supplier.id,
                                            },
                                            {
                                                onSuccess: () => {
                                                    toast.success(`${supplier.name} ${L('removed').toLowerCase()}`);
                                                    navigate(`..`);
                                                },
                                                onError: (err) => {
                                                    if (err instanceof TRPCClientError) toast.error(L('pg_unkown'));
                                                },
                                            },
                                        );
                                    },
                                    confirmVariant: 'danger',
                                    shouldPreventDefault: true,
                                }}
                            >
                                {L('remove')}
                            </ConfirmationButton>
                            <Button type="submit" style={{ marginRight: '0px' }}>
                                {L('save')}
                            </Button>
                        </ButtonContainer>
                    </SupplierForm>
                </RestyledCard>
            </Content>
        </>
    );
};
